/* eslint-disable no-return-await */
import axios from 'axios';
import { API_URL, API_URL_M7, getCachedSessionId, setCachedSessionId } from './common';

const authenticate = async (email, password) => {
  const { data } = await axios.post(`${API_URL}/v1/authorization`, {
    email,
    password
  });

  const { sessionId } = data;

  setCachedSessionId(sessionId);

  return { data };
};

const getSelf = async (sessionId) => {
  setCachedSessionId(sessionId);

  try {
    return await axios.get(`${API_URL}/v1/self`, {
      headers: { 'x-session-id': sessionId }
    });
  } catch (error) {
    return error;
  }
};

const updateSelf = async (body) => {
  try {
    return await axios.post(`${API_URL}/v1/auth/self/update`, body, {
      headers: { 'x-session-id': getCachedSessionId() }
    });
  } catch (error) {
    return error;
  }
};

const updatePassword = async (body) => {
  try {
    return await axios.post(`${API_URL}/v1/auth/self/updatePassword`, body, {
      headers: { 'x-session-id': getCachedSessionId() }
    });
  } catch (error) {
    return error;
  }
};

const verifyRequest = async (type, body) => {
  try {
    return await axios.post(`${API_URL}/v1/request/${type}`, body, {
      headers: { 'x-session-id': getCachedSessionId() }
    });
  } catch (error) {
    return error;
  }
};

const confirmVerifyRequest = async (type, body) => {
  try {
    return await axios.post(`${API_URL}/v1/confirm/${type}`, body, {
      headers: { 'x-session-id': getCachedSessionId() }
    });
  } catch (error) {
    return error;
  }
};

const createProject = async (body) => {
  try {
    return await axios.post(`${API_URL}/v1/project`, body, {
      headers: { 'x-session-id': getCachedSessionId() }
    });
  } catch (error) {
    return error;
  }
};

const deleteProject = async (id) => {
  try {
    return await axios.delete(`${API_URL}/v1/project/${id}`, {
      headers: { 'x-session-id': getCachedSessionId() }
    });
  } catch (error) {
    return error;
  }
};

const updateProject = async (projectId, data) => {
  try {
    return await axios.put(`${API_URL}/v1/project/${projectId}`, data, {
      headers: { 'x-session-id': getCachedSessionId() }
    });
  } catch (error) {
    return error;
  }
};

const getUsers = async (id) => {
  try {
    return await axios.get(`${API_URL}/v1/project/${id}/user`, {
      headers: { 'x-session-id': getCachedSessionId() }
    });
  } catch (error) {
    return error;
  }
};

const getPassword = async (id) => {
  try {
    return await axios.get(`${API_URL}/v1/project/${id}/user/generatePassword`, {
      headers: { 'x-session-id': getCachedSessionId() }
    });
  } catch (error) {
    return error;
  }
};

const createUser = async (id, data) => {
  try {
    return await axios.post(`${API_URL}/v1/project/${id}/user`, data, {
      headers: { 'x-session-id': getCachedSessionId() }
    });
  } catch (error) {
    return error;
  }
};

const updateUser = async (id, userId, data) => {
  try {
    return await axios.put(`${API_URL}/v1/project/${id}/user/${userId}`, data, {
      headers: { 'x-session-id': getCachedSessionId() }
    });
  } catch (error) {
    return error;
  }
};

const deleteUser = async (id, userId) => {
  try {
    return await axios.delete(`${API_URL}/v1/project/${id}/user/${userId}/deleteUser`, {
      headers: { 'x-session-id': getCachedSessionId() }
    });
  } catch (error) {
    return error;
  }
};

const deleteUserFromProject = async (id, userId) => {
  return await axios.delete(`${API_URL}/v1/project/${id}/user/${userId}/deleteUserFromProject`, {
    headers: { 'x-session-id': getCachedSessionId() }
  });
};

const getLanguages = async (id) => {
  try {
    return await axios.get(`${API_URL}/v1/project/${id}/languages`, {
      headers: { 'x-session-id': getCachedSessionId() }
    });
  } catch (error) {
    return error;
  }
};

const updateLanguages = async (id, data) => {
  try {
    return await axios.put(`${API_URL}/v1/project/${id}/languages`, data, {
      headers: { 'x-session-id': getCachedSessionId() }
    });
  } catch (error) {
    return error;
  }
};

const getCustomTypes = async (projectId) => {
  try {
    return await axios.get(`${API_URL}/v1/project/${projectId}/customType`, {
      headers: { 'x-session-id': getCachedSessionId() }
    });
  } catch (error) {
    return error;
  }
};

const getCustomTypeById = async (customTypeId, projectId) => {
  try {
    return await axios.get(`${API_URL}/v1/project/${projectId}/customType/${customTypeId}`, {
      headers: { 'x-session-id': getCachedSessionId() }
    });
  } catch (error) {
    return error;
  }
};

const getFiles = async (projectId) => {
  try {
    return await axios.get(`${API_URL}/v1/project/${projectId}/file`, {
      headers: { 'x-session-id': getCachedSessionId() }
    });
  } catch (error) {
    return error;
  }
};

const getContents = async (projectId, current, limit, searchItem = '', customTypeId = '') => {
  try {
    return await axios.get(
      `${API_URL}/v1/project/${projectId}/content/paginated?limit=${limit}&page=${current}&name=${searchItem}&customTypeId=${customTypeId}`,
      {
        headers: { 'x-session-id': getCachedSessionId() }
      }
    );
  } catch (error) {
    return error;
  }
};

const getContentById = async (contentId, projectId) => {
  try {
    return await axios.get(`${API_URL}/v1/project/${projectId}/content/${contentId}`, {
      headers: { 'x-session-id': getCachedSessionId() }
    });
  } catch (error) {
    return error;
  }
};

const downloadContent = async (projectId) => {
  try {
    return await axios.post(
      `${API_URL}/v1/project/${projectId}/content/download`,
      {},
      {
        headers: {
          Accept: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
          'x-session-id': getCachedSessionId()
        },
        responseType: 'arraybuffer'
      }
    );
  } catch (error) {
    return error;
  }
};

const getContentReviews = async (projectId) => {
  try {
    return await axios.get(`${API_URL}/v1/project/${projectId}/contentReview/`, {
      headers: { 'x-session-id': getCachedSessionId() }
    });
  } catch (error) {
    return error;
  }
};

const getContentReviewById = async (contentId, projectId) => {
  try {
    return await axios.get(`${API_URL}/v1/project/${projectId}/contentReview/${contentId}`, {
      headers: { 'x-session-id': getCachedSessionId() }
    });
  } catch (error) {
    return error;
  }
};

const updateContentReview = async (contentId, projectId, body) => {
  try {
    return await axios.put(`${API_URL}/v1/project/${projectId}/contentReview/${contentId}`, body, {
      headers: { 'x-session-id': getCachedSessionId() }
    });
  } catch (error) {
    return error;
  }
};

const deleteContentReview = async (contentId, projectId) => {
  try {
    return await axios.delete(`${API_URL}/v1/project/${projectId}/contentReview/${contentId}`, {
      headers: { 'x-session-id': getCachedSessionId() }
    });
  } catch (error) {
    return error;
  }
};

const createCustomType = async (customType, projectId) => {
  try {
    return await axios.post(`${API_URL}/v1/project/${projectId}/customType`, customType, {
      headers: { 'x-session-id': getCachedSessionId() }
    });
  } catch (error) {
    return error;
  }
};

const deleteCustomType = async (id, projectId) => {
  try {
    return await axios.delete(`${API_URL}/v1/project/${projectId}/customType/${id}`, {
      headers: { 'x-session-id': getCachedSessionId() }
    });
  } catch (error) {
    return error;
  }
};

const updateCustomType = async (customType, customTypeId, projectId) => {
  try {
    return await axios.put(
      `${API_URL}/v1/project/${projectId}/customType/${customTypeId}`,
      customType,
      {
        headers: { 'x-session-id': getCachedSessionId() }
      }
    );
  } catch (error) {
    return error;
  }
};

const updateOrder = async (body, projectId, url) => {
  try {
    return await axios.put(`${API_URL}/v1/project/${projectId}/${url}/reorder`, body, {
      headers: { 'x-session-id': getCachedSessionId() }
    });
  } catch (error) {
    return error;
  }
};

const updateContent = async (content, contentId, projectId) => {
  try {
    return await axios.put(`${API_URL}/v1/project/${projectId}/content/${contentId}`, content, {
      headers: { 'x-session-id': getCachedSessionId() }
    });
  } catch (error) {
    return error;
  }
};

const translateText = async (data, projectId) => {
  try {
    return await axios.post(`${API_URL}/v1/project/${projectId}/content/translate`, data, {
      headers: { 'x-session-id': getCachedSessionId() }
    });
  } catch (error) {
    return error;
  }
};

const uploadBanner = async (file, projectId) => {
  try {
    return await axios.post(`${API_URL}/v1/project/${projectId}/file/upload`, file, {
      headers: {
        'Content-Type': 'multipart/form-data',
        'x-session-id': getCachedSessionId()
      }
    });
  } catch (error) {
    return error;
  }
};

const deleteFile = async (id, projectId) => {
  try {
    return await axios.delete(`${API_URL}/v1/project/${projectId}/file/${id}`, {
      headers: { 'x-session-id': getCachedSessionId() }
    });
  } catch (error) {
    return error;
  }
};

const getImages = async (projectId) => {
  try {
    return await axios.get(`${API_URL}/v1/project/${projectId}/image`, {
      headers: { 'x-session-id': getCachedSessionId() }
    });
  } catch (error) {
    return error;
  }
};

const uploadBannerImage = async (file, projectId) => {
  try {
    return await axios.post(`${API_URL}/v1/project/${projectId}/image/upload`, file, {
      headers: {
        'Content-Type': 'multipart/form-data',
        'x-session-id': getCachedSessionId()
      }
    });
  } catch (error) {
    return error;
  }
};

const deleteImage = async (id, projectId) => {
  try {
    return await axios.delete(`${API_URL}/v1/project/${projectId}/image/${id}`, {
      headers: { 'x-session-id': getCachedSessionId() }
    });
  } catch (error) {
    return error;
  }
};

const createContent = async (content, projectId) => {
  try {
    return await axios.post(`${API_URL}/v1/project/${projectId}/content`, content, {
      headers: { 'x-session-id': getCachedSessionId() }
    });
  } catch (error) {
    return error;
  }
};

const deleteContent = async (id, projectId) => {
  try {
    return await axios.delete(`${API_URL}/v1/project/${projectId}/content/${id}`, {
      headers: { 'x-session-id': getCachedSessionId() }
    });
  } catch (error) {
    return error;
  }
};

const resetPassword = async (email) => {
  try {
    return await axios.post(`${API_URL}/v1/resetPassword`, email);
  } catch (error) {
    return error;
  }
};

const fetchCustomers = async (projectId) => {
  try {
    return await axios.get(`${API_URL}/v1/project/${projectId}/provider/authentication/customers`, {
      headers: { 'x-session-id': getCachedSessionId() }
    });
  } catch (error) {
    return error;
  }
};

const createCustomer = async (projectId, customersDraft) => {
  try {
    return await axios.post(`${API_URL}/v1/project/${projectId}/provider/user`, customersDraft, {
      headers: { 'x-session-id': getCachedSessionId() }
    });
  } catch (error) {
    return error;
  }
};

const fetchCustomer = async (projectId, customerId) => {
  try {
    return await axios.get(
      `${API_URL}/v1/project/${projectId}/provider/authentication/customer/${customerId}`,
      {
        headers: { 'x-session-id': getCachedSessionId() }
      }
    );
  } catch (error) {
    return error;
  }
};

const deleteCustomer = async (projectId, customerId) => {
  try {
    return await axios.delete(
      `${API_URL}/v1/project/${projectId}/provider/authentication/customer/${customerId}`,
      {
        headers: { 'x-session-id': getCachedSessionId() }
      }
    );
  } catch (error) {
    return error;
  }
};

const fetchCustomerProfile = async (projectId, profileId) => {
  try {
    return await axios.get(
      `${API_URL}/v1/project/${projectId}/ecommerce/profile/${profileId}/full`,
      {
        headers: { 'x-session-id': getCachedSessionId() }
      }
    );
  } catch (error) {
    return error;
  }
};

const updateCustomer = async (projectId, customerId, body) => {
  try {
    return await axios.put(
      `${API_URL}/v1/project/${projectId}/provider/authentication/customer/${customerId}`,
      body,
      {
        headers: { 'x-session-id': getCachedSessionId() }
      }
    );
  } catch (error) {
    return error;
  }
};

const updateCustomerProfile = async (projectId, profileId, body) => {
  try {
    return await axios.put(
      `${API_URL}/v1/project/${projectId}/ecommerce/profile/${profileId}`,
      body,
      {
        headers: { 'x-session-id': getCachedSessionId() }
      }
    );
  } catch (error) {
    return error;
  }
};

const getAllProviders = async (projectId) => {
  try {
    return await axios.get(`${API_URL}/v1/project/${projectId}/provider?limit=100`, {
      headers: { 'x-session-id': getCachedSessionId() }
    });
  } catch (error) {
    return error;
  }
};

const toggleProviderActivation = async (project, provider) => {
  try {
    return await axios.post(
      `${API_URL}/v1/project/${project._id}/provider/${provider.id}/${
        provider.isActive ? 'deactivate' : 'activate'
      }`,
      {
        project,
        provider
      },
      {
        headers: { 'x-session-id': getCachedSessionId() }
      }
    );
  } catch (error) {
    return error;
  }
};

const getAllMycoinsTransactions = async (projectId) => {
  try {
    return await axios.get(
      `${API_URL}/v1/project/${projectId}/provider/payment/mycoins/transactions`,
      {
        headers: { 'x-session-id': getCachedSessionId() }
      }
    );
  } catch (error) {
    return error;
  }
};

const downloadCustomerCsv = async (projectId) => {
  try {
    return await axios.get(
      `${API_URL}/v1/project/${projectId}/provider/authentication/customer/csv`,
      {
        headers: { 'x-session-id': getCachedSessionId() }
      }
    );
  } catch (error) {
    return error;
  }
};

const downloadMyCoinsTransactions = async (projectId) => {
  try {
    return await axios.get(
      `${API_URL}/v1/project/${projectId}/provider/payment/mycoins/transactions/csv`,
      {
        headers: { 'x-session-id': getCachedSessionId() }
      }
    );
  } catch (error) {
    return error;
  }
};

const getItems = async (projectId) => {
  try {
    return await axios.get(`${API_URL}/v1/project/${projectId}/ecommerce/item`, {
      headers: { 'x-session-id': getCachedSessionId() }
    });
  } catch (error) {
    return error;
  }
};

const getCategories = async (projectId) => {
  try {
    return await axios.get(`${API_URL}/v1/project/${projectId}/categories`, {
      headers: { 'x-session-id': getCachedSessionId() }
    });
  } catch (error) {
    return error;
  }
};

const getItem = async (projectId, itemId) => {
  try {
    return await axios.get(`${API_URL}/v1/project/${projectId}/ecommerce/item/${itemId}`, {
      headers: { 'x-session-id': getCachedSessionId() }
    });
  } catch (error) {
    return error;
  }
};

const updateItem = async (projectId, itemId, body) => {
  try {
    return await axios.put(`${API_URL}/v1/project/${projectId}/ecommerce/item/${itemId}`, body, {
      headers: { 'x-session-id': getCachedSessionId() }
    });
  } catch (error) {
    return error;
  }
};

const getPushNotifications = async (projectId) => {
  try {
    return await axios.get(`${API_URL}/v1/project/${projectId}/provider/pushNotification`, {
      headers: { 'x-session-id': getCachedSessionId() }
    });
  } catch (error) {
    return error;
  }
};

const downloadItemsToCsv = async (projectId) => {
  try {
    return await axios.get(`${API_URL}/v1/project/${projectId}/ecommerce/item/csv`, {
      headers: { 'x-session-id': getCachedSessionId() }
    });
  } catch (error) {
    return error;
  }
};

const getPushNotificationsTopics = async (projectId) => {
  try {
    return await axios.get(`${API_URL}/v1/project/${projectId}/provider/pushNotification/topic`, {
      headers: { 'x-session-id': getCachedSessionId() }
    });
  } catch (error) {
    return error;
  }
};

const removeItem = async (projectId, itemId) => {
  try {
    return await axios.delete(`${API_URL}/v1/project/${projectId}/ecommerce/item/${itemId}`, {
      headers: { 'x-session-id': getCachedSessionId() }
    });
  } catch (error) {
    return error;
  }
};

const createPushNotification = async (projectId, body) => {
  try {
    return await axios.post(`${API_URL}/v1/project/${projectId}/provider/pushNotification`, body, {
      headers: { 'x-session-id': getCachedSessionId() }
    });
  } catch (error) {
    return error;
  }
};

const createPushNotificationForUsers = async (projectId, body) => {
  try {
    return await axios.post(
      `${API_URL}/v1/project/${projectId}/provider/pushNotification/user`,
      body,
      {
        headers: { 'x-session-id': getCachedSessionId() }
      }
    );
  } catch (error) {
    return error;
  }
};

const getOrders = async (projectId) => {
  try {
    return await axios.get(
      `${API_URL_M7}/api_admin/v1/project/${projectId}/ecommerce/order/search`,
      {
        headers: { 'x-session-id': getCachedSessionId() }
      }
    );
  } catch (error) {
    return error;
  }
};

const getOrdersFiltered = async (projectId, body) => {
  try {
    return await axios.post(`${API_URL}/v1/project/${projectId}/ecommerce/order`, body, {
      headers: { 'x-session-id': getCachedSessionId() }
    });
  } catch (error) {
    return error;
  }
};

const getOrder = async (projectId, orderId) => {
  try {
    return await axios.get(`${API_URL}/v1/project/${projectId}/ecommerce/order/${orderId}`, {
      headers: { 'x-session-id': getCachedSessionId() }
    });
  } catch (error) {
    return error;
  }
};

const updateOrderStatus = async (projectId, orderId, body) => {
  try {
    return await axios.put(`${API_URL}/v1/project/${projectId}/ecommerce/order/${orderId}`, body, {
      headers: { 'x-session-id': getCachedSessionId() }
    });
  } catch (error) {
    return error;
  }
};

const getOrdersToCsv = async (projectId) => {
  try {
    return await axios.get(`${API_URL}/v1/project/${projectId}/ecommerce/order/csv`, {
      headers: { 'x-session-id': getCachedSessionId() }
    });
  } catch (error) {
    return error;
  }
};

const fetchCategories = async (projectId) => {
  try {
    return await axios.get(`${API_URL}/v1/project/${projectId}/categories`, {
      headers: { 'x-session-id': getCachedSessionId() }
    });
  } catch (error) {
    return error;
  }
};

const updateCategory = async (projectId, categoryId, body) => {
  try {
    return await axios.put(`${API_URL}/v1/project/${projectId}/category/${categoryId}`, body, {
      headers: { 'x-session-id': getCachedSessionId() }
    });
  } catch (error) {
    return error;
  }
};

const createCategory = async (projectId, body) => {
  try {
    return await axios.post(`${API_URL}/v1/project/${projectId}/category`, body, {
      headers: { 'x-session-id': getCachedSessionId() }
    });
  } catch (error) {
    return error;
  }
};

const fetchCategory = async (projectId, categoryId) => {
  try {
    return await axios.get(`${API_URL}/v1/project/${projectId}/category/${categoryId}`, {
      headers: { 'x-session-id': getCachedSessionId() }
    });
  } catch (error) {
    return error;
  }
};

const deleteCategory = async (projectId, categoryId) => {
  try {
    return await axios.delete(`${API_URL}/v1/project/${projectId}/category/${categoryId}`, {
      headers: { 'x-session-id': getCachedSessionId() }
    });
  } catch (error) {
    return error;
  }
};

const fetchClaims = async (projectId) => {
  try {
    return await axios.get(`${API_URL}/v1/project/${projectId}/claimManagement/claim/all`, {
      headers: { 'x-session-id': getCachedSessionId() }
    });
  } catch (error) {
    return error;
  }
};

const updateClaim = async (projectId, claimId, data) => {
  try {
    return await axios.put(
      `${API_URL}/v1/project/${projectId}/claimManagement/claim/${claimId}`,
      data,
      {
        headers: { 'x-session-id': getCachedSessionId() }
      }
    );
  } catch (error) {
    return error;
  }
};

const createClaimFile = async (projectId, claimId, fileKey) => {
  try {
    return await axios.put(
      `${API_URL}/v1/project/${projectId}/claimManagement/claim/${claimId}/file/${fileKey}`,
      {
        headers: { 'x-session-id': getCachedSessionId() }
      }
    );
  } catch (error) {
    return error;
  }
};

const deleteClaimFile = async (projectId, claimId, fileKey) => {
  try {
    return await axios.delete(
      `${API_URL}/v1/project/${projectId}/claimManagement/claim/${claimId}/file/${fileKey}`,
      {
        headers: { 'x-session-id': getCachedSessionId() }
      }
    );
  } catch (error) {
    return error;
  }
};

const generatePdf = async (projectId, claimId, type) => {
  try {
    return await axios.post(
      `${API_URL}/v1/project/${projectId}/claimManagement/claim/${claimId}/generatePdf/${type}`,
      {},
      {
        headers: { 'x-session-id': getCachedSessionId() }
      }
    );
  } catch (error) {
    return error;
  }
};

const getClaim = async (claimToken) => {
  try {
    return await axios.get(`${API_URL}/v2/claimManagement/claim/${claimToken}`, {
      headers: {
        'x-api-key': '9520febd4a5d1c366f6abc81fc8aa5a7503e24fc44e8585c',
        'x-session-id': getCachedSessionId()
      }
    });
  } catch (error) {
    return error;
  }
};

const getCompensationAmount = async (depAirId, arrAirId) => {
  try {
    return await axios.get(
      `${API_URL}/v2/claimManagement/compensationAmount/${depAirId}/${arrAirId}`,
      {
        headers: {
          'x-api-key': '9520febd4a5d1c366f6abc81fc8aa5a7503e24fc44e8585c',
          'x-session-id': getCachedSessionId()
        }
      }
    );
  } catch (error) {
    return error;
  }
};

const searchAirports = async (value) => {
  try {
    return await axios.get(`${API_URL}/v2/claimManagement/airports?page=1&limit=10&name=${value}`, {
      headers: {
        'x-api-key': '9520febd4a5d1c366f6abc81fc8aa5a7503e24fc44e8585c',
        'x-session-id': getCachedSessionId()
      }
    });
  } catch (error) {
    return error;
  }
};

const uploadClaimFile = async (projectId, claimId, fileKey, file) => {
  try {
    return await axios.put(
      `${API_URL}/v1/project/${projectId}/claimManagement/claim/${claimId}/file/${fileKey}`,
      file,
      {
        headers: { 'x-session-id': getCachedSessionId() }
      }
    );
  } catch (error) {
    return error;
  }
};

const removeClaimFile = async (projectId, claimId, fileKey, fileId) => {
  try {
    return await axios.delete(
      `${API_URL}/v1/project/${projectId}/claimManagement/claim/${claimId}/file/${fileKey}/${fileId}`,
      {
        headers: { 'x-session-id': getCachedSessionId() }
      }
    );
  } catch (error) {
    return error;
  }
};

const removeClaimFileWithKey = async (projectId, claimId, fileKey) => {
  try {
    return await axios.delete(
      `${API_URL}/v1/project/${projectId}/claimManagement/claim/${claimId}/file/${fileKey}`,
      {
        headers: { 'x-session-id': getCachedSessionId() }
      }
    );
  } catch (error) {
    return error;
  }
};

const removeClaim = async (projectId, claimId) => {
  try {
    return await axios.delete(
      `${API_URL}/v1/project/${projectId}/claimManagement/claim/${claimId}`,
      {
        headers: { 'x-session-id': getCachedSessionId() }
      }
    );
  } catch (error) {
    return error;
  }
};

const fetchEmailSubscription = async (projectId) => {
  try {
    return await axios.get(`${API_URL}/v1/project/${projectId}/provider/emailSubscription `, {
      headers: { 'x-session-id': getCachedSessionId() }
    });
  } catch (error) {
    return error;
  }
};

const downloadEmailSubscriptionCsv = async (projectId) => {
  try {
    return await axios.get(`${API_URL}/v1/project/${projectId}/provider/emailSubscription/csv `, {
      headers: { 'x-session-id': getCachedSessionId() }
    });
  } catch (error) {
    return error;
  }
};

const getDelivery = async (projectId) => {
  try {
    return await axios.get(`${API_URL}/v1/project/${projectId}/ecommerce/delivery`, {
      headers: { 'x-session-id': getCachedSessionId() }
    });
  } catch (error) {
    return error;
  }
};

const fetchEmailNotification = async (projectId) => {
  try {
    return await axios.get(`${API_URL}/v1/project/${projectId}/provider/notification/email `, {
      headers: { 'x-session-id': getCachedSessionId() }
    });
  } catch (error) {
    return error;
  }
};

const updateDelivery = async (projectId, deliveryId, body) => {
  try {
    return await axios.put(
      `${API_URL}/v1/project/${projectId}/ecommerce/delivery/${deliveryId}`,
      body,
      {
        headers: { 'x-session-id': getCachedSessionId() }
      }
    );
  } catch (error) {
    return error;
  }
};

const downloadEmailNotificationCsv = async (projectId) => {
  try {
    return await axios.get(`${API_URL}/v1/project/${projectId}/provider/notification/email/csv `, {
      headers: { 'x-session-id': getCachedSessionId() }
    });
  } catch (error) {
    return error;
  }
};

const fetchTextNotification = async (projectId) => {
  try {
    return await axios.get(
      `${API_URL}/v1/project/${projectId}/provider/notification/textMessage `,
      {
        headers: { 'x-session-id': getCachedSessionId() }
      }
    );
  } catch (error) {
    return error;
  }
};

const downloadTextNotificationCsv = async (projectId) => {
  try {
    return await axios.get(
      `${API_URL}/v1/project/${projectId}/provider/notification/textMessage/csv `,
      {
        headers: { 'x-session-id': getCachedSessionId() }
      }
    );
  } catch (error) {
    return error;
  }
};

const getContentsFromContentModel = async (contentModelID, xApiKey) => {
  try {
    return await axios.get(`${API_URL}/${contentModelID}`, {
      headers: { 'x-api-key': xApiKey, 'x-session-id': getCachedSessionId() }
    });
  } catch (error) {
    return error;
  }
};

const sendLawyerEmail = async (body, xApiKey) => {
  try {
    return await axios.post(`${API_URL}/v2_api/notification/sendEmail`, body, {
      headers: { 'x-api-key': xApiKey, 'x-session-id': getCachedSessionId() }
    });
  } catch (error) {
    return error;
  }
};

const downloadClaimFiles = async (projectId, claimId, body) => {
  try {
    return await axios.post(
      `${API_URL}/v1/project/${projectId}/claimManagement/claim/${claimId}/downloadFiles`,
      body,
      {
        headers: { 'x-session-id': getCachedSessionId() }
      }
    );
  } catch (error) {
    return error;
  }
};

const downloadClaimCsv = async (projectId) => {
  try {
    return await axios.get(`${API_URL}/v1/project/${projectId}/claimManagement/claim/csv`, {
      headers: { 'x-session-id': getCachedSessionId() }
    });
  } catch (error) {
    return error;
  }
};

const downloadTripsCsv = async (projectId) => {
  try {
    const resp = await axios.get(`${API_URL}/v1/project/${projectId}/external/trip/csv`, {
      headers: { 'x-session-id': getCachedSessionId() }
    });

    return resp;
  } catch (error) {
    return error;
  }
};

const downloadTicketsCsv = async (projectId) => {
  try {
    const resp = await axios.get(`${API_URL}/v1/project/${projectId}/external/trip/tickets/csv`, {
      headers: { 'x-session-id': getCachedSessionId() }
    });

    return resp;
  } catch (error) {
    return error;
  }
};

const activateGsuite = async (projectId, domainId) => {
  try {
    return await axios.post(
      `${API_URL}/v1/project/${projectId}/provider/domain/${domainId}/email/activateGsuite`,
      {},
      {
        headers: { 'x-session-id': getCachedSessionId() }
      }
    );
  } catch (error) {
    return error;
  }
};

const activateMdello = async (projectId, domainId) => {
  try {
    return await axios.post(
      `${API_URL}/v1/project/${projectId}/provider/domain/${domainId}/email/activateMdello`,
      {},
      {
        headers: { 'x-session-id': getCachedSessionId() }
      }
    );
  } catch (error) {
    return error;
  }
};

const deactivate = async (projectId, domainId) => {
  try {
    return await axios.post(
      `${API_URL}/v1/project/${projectId}/provider/domain/${domainId}/email/deactivate`,
      {},
      {
        headers: { 'x-session-id': getCachedSessionId() }
      }
    );
  } catch (error) {
    return error;
  }
};

const getAllComments = async (projectId) => {
  try {
    return await axios.get(`${API_URL}/v1/project/${projectId}/ecommerce/item/comments `, {
      headers: { 'x-session-id': getCachedSessionId() }
    });
  } catch (error) {
    return error;
  }
};

const deleteComment = async (projectId, categoryId) => {
  try {
    return await axios.delete(
      `${API_URL}/v1/project/${projectId}/ecommerce/item/comment/${categoryId}`,
      {
        headers: { 'x-session-id': getCachedSessionId() }
      }
    );
  } catch (error) {
    return error;
  }
};

const getComplains = async (projectId) => {
  try {
    return await axios.get(`${API_URL}/v1/project/${projectId}/ecomReport`, {
      headers: { 'x-session-id': getCachedSessionId() }
    });
  } catch (error) {
    return error;
  }
};

const updateComplain = async (projectId, reportId, body) => {
  try {
    return await axios.put(`${API_URL}/v1/project/${projectId}/ecomReport/${reportId}`, body, {
      headers: { 'x-session-id': getCachedSessionId() }
    });
  } catch (error) {
    return error;
  }
};

const deleteComplain = async (projectId, reportId) => {
  try {
    return await axios.delete(`${API_URL}/v1/project/${projectId}/ecomReport/${reportId}`, {
      headers: { 'x-session-id': getCachedSessionId() }
    });
  } catch (error) {
    return error;
  }
};

const getPaymentSubscriptions = async (projectId) => {
  try {
    return await axios.get(`${API_URL}/v1/project/${projectId}/provider/payment/subscription `, {
      headers: { 'x-session-id': getCachedSessionId() }
    });
  } catch (error) {
    return error;
  }
};

const getIPay = async (projectId) => {
  try {
    return await axios.get(
      `${API_URL}/v1/project/${projectId}/provider/payment/ipay/transactions`,
      {
        headers: { 'x-session-id': getCachedSessionId() }
      }
    );
  } catch (error) {
    return error;
  }
};

const downloadIPayCsv = async (projectId) => {
  try {
    return await axios.get(
      `${API_URL}/v1/project/${projectId}/provider/payment/ipay/transactions/csv `,
      {
        headers: { 'x-session-id': getCachedSessionId() }
      }
    );
  } catch (error) {
    return error;
  }
};

const updateSubscriptionDate = async (projectId, subscriptionId, body) => {
  try {
    return await axios.post(
      `${API_URL}/v1/project/${projectId}/provider/payment/subscription/${subscriptionId}`,
      body,
      {
        headers: { 'x-session-id': getCachedSessionId() }
      }
    );
  } catch (error) {
    return error;
  }
};

const duplicateContentModal = async (projectId, customTypeId, body) => {
  try {
    return await axios.post(
      `${API_URL}/v1/project/${projectId}/customType/${customTypeId}/duplicate`,
      body,
      {
        headers: { 'x-session-id': getCachedSessionId() }
      }
    );
  } catch (error) {
    return error;
  }
};

const duplicateContent = async (projectId, body) => {
  try {
    return await axios.post(`${API_URL}/v1/project/${projectId}/content/duplicate`, body, {
      headers: { 'x-session-id': getCachedSessionId() }
    });
  } catch (error) {
    return error;
  }
};
const getReviews = async (projectId) => {
  try {
    return await axios.get(`${API_URL}/v1/project/${projectId}/ecommerce/order/reviews `, {
      headers: { 'x-session-id': getCachedSessionId() }
    });
  } catch (error) {
    return error;
  }
};

const deleteReview = async (projectId, orderId) => {
  try {
    return await axios.delete(
      `${API_URL}/v1/project/${projectId}/ecommerce/order/${orderId}/review`,
      {
        headers: { 'x-session-id': getCachedSessionId() }
      }
    );
  } catch (error) {
    return error;
  }
};

const getPayzeProviders = async (projectId) => {
  try {
    return await axios.get(
      `${API_URL}/v1/project/${projectId}/provider/payment/payze/transactions `,
      {
        headers: { 'x-session-id': getCachedSessionId() }
      }
    );
  } catch (error) {
    return error;
  }
};

const downloadPayzeProviderCsv = async (projectId) => {
  try {
    return await axios.get(
      `${API_URL}/v1/project/${projectId}/provider/payment/payze/transactions/csv`,
      {
        headers: { 'x-session-id': getCachedSessionId() }
      }
    );
  } catch (error) {
    return error;
  }
};

export {
  authenticate,
  getSelf,
  updateSelf,
  updatePassword,
  verifyRequest,
  confirmVerifyRequest,
  createProject,
  updateProject,
  deleteProject,
  getCustomTypes,
  getCustomTypeById,
  getFiles,
  getContents,
  getContentById,
  downloadContent,
  getContentReviews,
  getContentReviewById,
  deleteContentReview,
  updateContentReview,
  createCustomType,
  deleteCustomType,
  updateCustomType,
  updateOrder,
  translateText,
  uploadBanner,
  updateContent,
  deleteFile,
  createContent,
  deleteContent,
  resetPassword,
  getLanguages,
  updateLanguages,
  fetchCustomers,
  createCustomer,
  fetchCustomer,
  deleteCustomer,
  fetchCustomerProfile,
  updateCustomer,
  updateCustomerProfile,
  downloadCustomerCsv,
  getUsers,
  updateUser,
  getPassword,
  createUser,
  deleteUser,
  getAllProviders,
  toggleProviderActivation,
  getAllMycoinsTransactions,
  downloadMyCoinsTransactions,
  getItems,
  getItem,
  getCategories,
  updateItem,
  downloadItemsToCsv,
  removeItem,
  getPushNotifications,
  getPushNotificationsTopics,
  createPushNotification,
  createPushNotificationForUsers,
  getOrders,
  getOrdersFiltered,
  getOrder,
  updateOrderStatus,
  getOrdersToCsv,
  fetchCategories,
  updateCategory,
  fetchCategory,
  deleteCategory,
  createCategory,
  fetchClaims,
  updateClaim,
  createClaimFile,
  deleteClaimFile,
  generatePdf,
  getClaim,
  uploadClaimFile,
  removeClaimFile,
  getCompensationAmount,
  removeClaimFileWithKey,
  searchAirports,
  fetchEmailSubscription,
  downloadEmailSubscriptionCsv,
  getDelivery,
  updateDelivery,
  fetchEmailNotification,
  downloadEmailNotificationCsv,
  fetchTextNotification,
  downloadTextNotificationCsv,
  getContentsFromContentModel,
  sendLawyerEmail,
  downloadClaimFiles,
  activateGsuite,
  activateMdello,
  deactivate,
  getAllComments,
  deleteComment,
  getComplains,
  updateComplain,
  deleteComplain,
  deleteUserFromProject,
  getPaymentSubscriptions,
  updateSubscriptionDate,
  getIPay,
  downloadIPayCsv,
  duplicateContentModal,
  duplicateContent,
  getReviews,
  deleteReview,
  getPayzeProviders,
  downloadPayzeProviderCsv,
  getImages,
  uploadBannerImage,
  deleteImage,
  downloadClaimCsv,
  removeClaim,
  downloadTripsCsv,
  downloadTicketsCsv
};
